import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import MainPageLayout from "../../components/header/MainPageLayout";
import Table from "../../components/table/Table";
import Pill from "../viewInstitutionsPage/components/Pill";
import {
    TableCard,
    PageTitleSection,
    PageTitle,
    ActionsCard,
    ActionButtons,
    CreateButtons,
} from "../viewInstitutionsPage/components/InstitutionsTable.styles";
import { GetAllDepartmentsType } from "../../core/ServerApiLayer/VOne/institutions/get_departments";
import { getJwt } from "../../core/LocalStorage/AuthSession/core";
import { Department } from "../../core/ServerApiLayer/VOne/institutions/get_departments";
import BackButton from "../../components/button/BackButton/BackButton";
import CreateDepartmentModal from "../../components/modal/create-department-modal/CreateDepartmentModal";
import { CreateDepartmentType } from "../../core/ServerApiLayer/VOne/institutions/create_department";

interface DepartmentTableProps {
    getAllDepartments: GetAllDepartmentsType;
    createDepartment: CreateDepartmentType;
}

export const DepartmentTable: React.FC<DepartmentTableProps> = ({
    getAllDepartments,
    createDepartment,
}) => {
    const [departments, setDepartments] = useState<Department[]>([]);
    // Starts true: the first render happens before the request has been sent,
    // and an empty table is not yet an answer.
    const [loading, setLoading] = useState(true);
    const { institutionId } = useParams();

    // create institution form data
    const [formData, setFormData] = useState({
        name: "",
    });

    // State to open and close the create modal
    const [openCreateDepartmentModal, setOpenCreateDepartmentModal] =
        useState(false);

    const navigate = useNavigate();

    // The list endpoint is the only source of a full department row.
    const loadDepartments = useCallback(async () => {
        const jwt = getJwt();
        if (!jwt) {
            console.error("JWT expired or missing.");
            return;
        }

        // The API layer answers a network failure with a 500 rather than a
        // rejection, so reaching the catch means something unforeseen. The
        // table keeps whatever it already had.
        try {
            const response = await getAllDepartments(
                jwt,
                Number(institutionId),
            );
            if (response.status === 200 && response.data) {
                setDepartments(
                    response.data.map((dept) => ({
                        entity_id: dept.entity_id,
                        institution_id: dept.institution_id,
                        name: dept.name,
                        no_of_licenses: dept.no_of_licenses,
                        no_of_users: dept.no_of_users,
                        status: dept.status as
                            | "active"
                            | "inactive"
                            | "free trial",
                    })),
                );
            }
        } catch (error) {
            console.error("Error loading departments:", error);
        }
    }, [getAllDepartments, institutionId]);

    useEffect(() => {
        loadDepartments().finally(() => setLoading(false));
    }, [loadDepartments]);

    const columns = [
        {
            key: "institution_id",
            label: "Institution ID",
        },
        {
            key: "name",
            label: "Department Name",
        },
        {
            key: "status",
            label: "Status",
            render: (row: Department) => <Pill>{row.status}</Pill>,
        },
        {
            key: "no_of_licenses",
            label: "No. of Licenses",
        },
        {
            key: "no_of_users",
            label: "No. of Active Users",
        },
        {
            key: "actions",
            label: "Actions",
            render: (row: Department) => (
                <ActionsCard>
                    <ActionButtons
                        backgroundColorHex="#278230"
                        onClick={() =>
                            navigate(
                                `/institution/${institutionId}/department/${row.entity_id}`,
                            )
                        }
                    >
                        View Users
                    </ActionButtons>
                </ActionsCard>
            ),
        },
    ];

    const onRequestClose = () => {
        setOpenCreateDepartmentModal(false);
        clearFormData();
    };

    const clearFormData = () => {
        //Clear modal after submission
        setFormData({ name: "" });
    };

    const handleCreateDepartment = async (e: any) => {
        e.preventDefault();

        const jwt = getJwt();
        if (!jwt) {
            console.error("JWT expired or missing.");
            return;
        }

        const response = await createDepartment(
            jwt,
            Number(institutionId),
            formData.name,
        );
        if (response.status === 201 && response.department) {
            // The create response is the inserted row: an id, a name and the
            // institution it belongs to. The status, the counts and the entity
            // id the "View" link is built from come from the list endpoint, so
            // the list is read back.
            await loadDepartments();
            setOpenCreateDepartmentModal(false);
            clearFormData();
        } else {
            console.error("Error creating department:", response.error);
        }
    };

    return (
        <MainPageLayout>
            <BackButton
                navigate={navigate}
                to={`/institution/${institutionId}`}
            />
            <TableCard>
                <PageTitleSection>
                    <PageTitle>Departments</PageTitle>
                    <CreateButtons
                        backgroundColorHex="#135185"
                        onClick={() => setOpenCreateDepartmentModal(true)}
                    >
                        Create Department
                    </CreateButtons>
                </PageTitleSection>

                <Table
                    data={departments}
                    //@ts-ignore
                    columns={columns}
                    loading={loading}
                    loadingText="Loading departments…"
                    noRowsText="No departments found"
                />
            </TableCard>
            {openCreateDepartmentModal && (
                <CreateDepartmentModal
                    onSubmit={handleCreateDepartment}
                    onRequestClose={onRequestClose}
                    setFormData={setFormData}
                    formData={formData}
                />
            )}
        </MainPageLayout>
    );
};

export default DepartmentTable;
